import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';

import { getHeaders } from '@core/actions/xhrRequest';

// Add Funds modal — rebuilt on the same design vocabulary as the
// account's referral / profile pages: white card, 3px radius, the
// signature blue-purple drop shadow, unified 40/3px cyan-solid CTA.
//
// Submit flow:
//   1. POST `/api/payment/balance-checkout-url` with `{amount}`.
//   2. Customer middleware chooses an available gateway and returns its
//      hosted checkout URL.
//   3. The gateway returns to `/api/payment/balance-success`; server-side
//      handlers confirm/record the deposit, then redirect back to balance.
//   4. BalancePage picks up the query flag, shows a toast, and re-fetches.
export default function AddFundsModal({ open, onClose }) {
    const [amount, setAmount] = useState('');
    const [amountError, setAmountError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const inputRef = useRef(null);
    // Same geo block-list that gates the order pay CTAs (state.user.geoBlocked,
    // sourced from /api/user/user → CountryManager::isBlocked()). A blocked
    // country can't deposit either, so disable the Load CTA up front.
    const blocked = useSelector(state => !!state.user?.geoBlocked);

    // Lock body scroll while modal is open so the cabinet page behind
    // doesn't scroll under the customer's finger on mobile. Also
    // pulls focus into the amount input for keyboard-first users.
    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const t = setTimeout(() => inputRef.current?.focus(), 80);
        return () => {
            document.body.style.overflow = prev;
            clearTimeout(t);
        };
    }, [open]);

    // Esc → close. Keeps the modal keyboard-accessible without a
    // trap library.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    const onChangeAmount = (e) => {
        setAmount(e.target.value);
        if (e.target.value) setAmountError('');
    };

    const onBeforeInput = (e) => {
        // Block any character that would produce an invalid money
        // token. Works for paste + IME + regular typing (unlike the
        // old `keypress`-based validator which missed paste).
        const nextChar = e.data ?? '';
        if (!nextChar) return;
        const projected = (amount || '') + nextChar;
        if (!/^[0-9]*(?:\.\d{0,2})?$/.test(projected)) {
            e.preventDefault();
        }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (submitting) return;
        if (blocked) {
            setAmountError('Payment unavailable');
            return;
        }

        const n = Number(amount);
        if (!amount || Number.isNaN(n)) {
            setAmountError('Enter an amount.');
            return;
        }
        if (n <= 0) {
            setAmountError('Amount must be greater than 0.');
            return;
        }
        if (n > 9999) {
            setAmountError('Amount must be less than 9999.');
            return;
        }
        if (n < 0.55) {
            setAmountError('Minimum amount is $0.55.');
            return;
        }

        setAmountError('');
        setSubmitting(true);
        try {
            const form = new URLSearchParams();
            form.append('amount', String(n));
            const res = await axios.post(
                '/api/payment/balance-checkout-url',
                form,
                {
                    withCredentials: true,
                    headers: {
                        ...getHeaders(),
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                },
            );
            const url = res?.data?.url || res?.data?.data?.url;
            if (!url) {
                setAmountError('Couldn’t start the checkout. Please try again.');
                setSubmitting(false);
                return;
            }
            // Hard nav — Stripe hosts the card form, success URL brings
            // the browser back to `/account/balance?payment=success`.
            window.location.assign(url);
        } catch (err) {
            setAmountError(err?.response?.data?.message || 'Couldn’t start the checkout.');
            setSubmitting(false);
        }
    };

    return (
        <div
            className="bidding-add-funds__backdrop"
            onClick={onClose}
            role="presentation"
        >
            <div
                className="bidding-add-funds__card"
                role="dialog"
                aria-modal="true"
                aria-labelledby="bidding-add-funds__title"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    type="button"
                    className="bidding-add-funds__close"
                    onClick={onClose}
                    aria-label="Close"
                >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M6 6l12 12M18 6L6 18"/>
                    </svg>
                </button>

                <div className="bidding-add-funds__head">
                    <h2 id="bidding-add-funds__title" className="bidding-add-funds__title">
                        Add funds to your balance
                    </h2>
                    <p className="bidding-add-funds__lead">
                        Keep a balance ready and pay for new orders in one click, no
                        card needed each time.
                    </p>
                </div>

                <form
                    onSubmit={onSubmit}
                    className="bidding-add-funds__form"
                    id="js-add-balance-form"
                >
                    <label className="bidding-add-funds__field">
                        <span className="bidding-add-funds__label">Amount to load</span>
                        <span
                            className={`bidding-add-funds__input${amountError ? ' has-error' : ''}`}
                        >
                            <span className="bidding-add-funds__input-prefix" aria-hidden="true">$</span>
                            <input
                                ref={inputRef}
                                type="text"
                                name="amount"
                                inputMode="decimal"
                                autoComplete="off"
                                value={amount}
                                onChange={onChangeAmount}
                                onBeforeInput={onBeforeInput}
                                className="bidding-add-funds__input-control"
                                placeholder="0"
                                aria-invalid={amountError ? 'true' : undefined}
                            />
                        </span>
                        {amountError && (
                            <span className="bidding-add-funds__error">{amountError}</span>
                        )}
                    </label>

                    <p className="bidding-add-funds__terms">
                        By clicking &quot;Load&quot; you agree to our{' '}
                        <a className="bidding-add-funds__link" href="/terms-and-conditions">Terms and Conditions</a>
                        {' '}and{' '}
                        <a className="bidding-add-funds__link" href="/privacy-policy">Privacy Policy</a>.
                    </p>

                    {blocked && (
                        <div className="bidding-add-funds__blocked" role="alert">
                            Payment unavailable
                        </div>
                    )}

                    <div className="bidding-add-funds__actions">
                        <button
                            type="button"
                            className="bidding-add-funds__cancel"
                            onClick={onClose}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="bidding-add-funds__submit"
                            disabled={submitting || blocked}
                        >
                            {submitting ? 'Loading…' : 'Load'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
